import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { subMonths } from 'date-fns';
import { useNotify } from '@/components/snackbar';
import { useCallback, useState } from 'react';
import { hasMultiCurrency } from '@/utils/multicurrency';
import { useSessionContext } from '../auth/useSessionContext';
import { clearReportsCache, exportReport } from '../services/reports';
import {
  DEFAULT_PRODUCT_CURRENCY_CODE,
  DOLARIZATION_SUPPORTED_CURRENCIES,
  resolveReportCurrencyForApi,
} from '../utils/currency';
import { fDate } from '../utils/formatTime';
import useTableFilter from './useTableFilter';

export const getDefaultPastMonthRange = () => ({
  startDate: subMonths(new Date(), 1),
  endDate: new Date(),
});

export const formatRangeDates = (dates) => ({
  startDate: fDate(dates?.startDate || new Date(), 'dd-MM-yyyy'),
  endDate: fDate(dates?.endDate || new Date(), 'dd-MM-yyyy'),
});

/**
 * Núcleo compartilhado dos relatórios financeiros do dashboard.
 * Concentra paginação, intervalo de datas, seleção de moeda, consulta paginada e exportação.
 * As diferenças reais de cada relatório entram como configuração explícita.
 *
 * @param {object} config
 * @param {string} config.report — segmento usado nos endpoints de exportação
 * @param {() => object} config.getDefaultDates — intervalo inicial não formatado
 * @param {(dates: object) => object} config.formatDates — datas no formato exigido pelo endpoint
 * @param {(params: object) => Array} config.buildQueryKey
 * @param {(params: object) => Promise} config.fetchReport
 * @param {(data: any) => any} [config.select]
 * @param {any} [config.initialData]
 * @param {object} [config.tableFilterOptions]
 * @param {string} [config.defaultType]
 * @param {(args: any) => object} config.buildExportParams
 * @param {Array} [config.invalidateQueryKey] — quando ausente, exportar não limpa cache nem invalida
 */
const useReportController = ({
  report,
  getDefaultDates,
  formatDates,
  buildQueryKey,
  fetchReport,
  select,
  initialData,
  tableFilterOptions,
  defaultType,
  buildExportParams,
  invalidateQueryKey,
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { user } = useSessionContext();
  const hasMultiCurrencyFeature = hasMultiCurrency(user);

  const table = useTableFilter(tableFilterOptions);
  const [type, setType] = useState(defaultType);
  const [rawDates, setDates] = useState(getDefaultDates);
  const [currency, setCurrencyState] = useState(DEFAULT_PRODUCT_CURRENCY_CODE);

  const setCurrency = useCallback(
    (value) => {
      setCurrencyState(value);
      table.setPage(1);
    },
    [table]
  );

  const dates = formatDates(rawDates);
  const apiCurrency = resolveReportCurrencyForApi(currency, hasMultiCurrencyFeature);

  const { data, isLoading: fetching } = useQuery({
    queryKey: buildQueryKey({ page: table.page, type, dates, rawDates, currency: apiCurrency }),
    queryFn: () => fetchReport({ page: table.page, type, dates, currency: apiCurrency }),
    ...(select ? { select } : {}),
    ...(initialData ? { initialData } : {}),
  });

  const { mutateAsync: mutateExport, isLoading: loadingExport } = useMutation({
    async mutationFn(args) {
      await exportReport({
        report,
        ...buildExportParams(args),
        ...dates,
        currency: apiCurrency,
      });
    },
    onSuccess() {
      if (invalidateQueryKey) {
        clearReportsCache();
        queryClient.invalidateQueries({ queryKey: invalidateQueryKey });
      }
      notify.success('Exportação realizada com sucesso');
    },
  });

  return {
    table,
    data,
    fetching,
    dates: rawDates,
    setDates,
    type,
    setType,
    currency,
    setCurrency,
    currencyOptions: DOLARIZATION_SUPPORTED_CURRENCIES,
    hasMultiCurrencyFeature,
    mutateExport,
    loadingExport,
  };
};

export default useReportController;
